import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { isFulfilled } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { DialogBox } from "../../../../common/components/functional/dialogBox/DialogBox";
import { OrderEdit, TalentWorkOfferStatus } from "../../orderAPI";
import {
  cancelWorkOffers,
  findManualOfferTalentsForBundle,
  getWorkOffersState,
  selectWorkOffersByBundleId,
  sendManualOffer,
} from "../../workOffersSlice";
import ManualOfferTalentList from "./ManualOfferTalentList";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { VALID_NAME_REGEX } from "../../../../common/helpers/regexes";
import { TALENT_OFFER_NOT_OFFERED } from "../../constants";
import { sortByName } from "../../../../common/helpers/common";

interface Props {
  isOpen: boolean;
  closeDialog: () => void;
  orderRow: OrderEdit;
}

export const ManualOfferDialog = ({
  isOpen,
  closeDialog,
  orderRow,
}: Props) => {
  const { t } = useTranslation(["common", "order"]);
  const dispatch = useAppDispatch();

  const { status: workOffersStatus } = useAppSelector(
    (state) => state.workOffers,
  );
  const isLoading = workOffersStatus === "pending";

  const manuallyOfferedTalents =
    useAppSelector((state) =>
      selectWorkOffersByBundleId(state, orderRow.bundleId),
    )?.manuallyOffered || [];
  const automatchedTalents =
    useAppSelector((state) =>
      selectWorkOffersByBundleId(state, orderRow.bundleId),
    )?.automatched || [];
  const talents = manuallyOfferedTalents.concat(automatchedTalents);
  const [suggestedTalents, setSuggestedTalents] =
    useState<TalentWorkOfferStatus[]>();
  const [talentSearchString, setTalentSearchString] = useState("");

  const cancelDialog = () => {
    setTalentSearchString("");
    closeDialog();
  };

  const cancelOffer = (talentId: string) => {
    const talent = talents.find((t) => t.id === talentId);
    dispatch(
      cancelWorkOffers({
        talentIds: [talentId],
        bundleId: orderRow.bundleId,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        if (talent) {
          const suggestedTalent = suggestedTalents?.find(
            (t) => t.id === talentId,
          );
          if (!suggestedTalent) {
            setSuggestedTalents([
              ...(suggestedTalents || []),
              { ...talent, workOfferStatus: TALENT_OFFER_NOT_OFFERED },
            ]);
          }
        }
      }
    });
  };

  const sendOffer = (talent: TalentWorkOfferStatus) => {
    dispatch(
      sendManualOffer({
        talentId: talent.id,
        firstName: talent.firstName,
        lastName: talent.lastName,
        bundleId: orderRow.bundleId,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setSuggestedTalents(
          suggestedTalents?.filter((t) => t.id !== talent.id),
        );
      }
    });
  };

  const validateSearchString = (searchString: string) => {
    return VALID_NAME_REGEX.test(searchString.toLowerCase());
  };

  useEffect(() => {
    if (!orderRow.bundleId) return;
    dispatch(getWorkOffersState({ bundleId: orderRow.bundleId }));
  }, [dispatch, orderRow.bundleId]);

  useEffect(() => {
    if (!orderRow.bundleId) return;
    dispatch(
      findManualOfferTalentsForBundle({
        search: talentSearchString,
        bundleId: orderRow.bundleId,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setSuggestedTalents(action.payload);
      }
    });
  }, [talentSearchString, orderRow.bundleId, dispatch]);

  // Filter suggestedTalents with talents that are already offered
  const filteredSuggestedTalents = sortByName(
    suggestedTalents?.filter(
      (talent) => !talents.some((t) => t.id === talent.id),
    ) || [],
  );

  const steps = [
    {
      label: "",
      component: (
        <>
          <OverlaySpinner isLoading={isLoading} />
          <ManualOfferTalentList
            header={t("order:talentsOfferedShift")}
            talents={sortByName(talents)}
            cancelOffer={cancelOffer}
            sendOffer={sendOffer}
            visible={talents.length > 0}
          />
          <ManualOfferTalentList
            header={t("order:talentsSuitableForShift")}
            talents={filteredSuggestedTalents || []}
            cancelOffer={cancelOffer}
            sendOffer={sendOffer}
          />
        </>
      ),
    },
  ];

  return (
    <DialogBox
      isOpen={isOpen}
      dialogTitle={t("common:searchTalents")}
      onCancelDialog={cancelDialog}
      steps={steps}
      cancelLabel={t("close")}
      finishLabel={t("save")}
      hasSearchField={true}
      updateSearchString={(searchString: string) =>
        setTalentSearchString(searchString.toLowerCase())
      }
      validateSearchString={validateSearchString}
      searchStringInvalidText={t("errors:invalidSearchString") || ""}
    />
  );
};
